import { useEffect, useEffectEvent, useState } from "react";
import {
  Alert,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";
import type { z } from "zod";

import type {
  Assessment,
  AssessmentId,
  DeleteAssessmentRequest,
  FormProgress,
} from "@thalamos/haven-common";
import { DeleteAssessmentRequestSchema } from "@thalamos/haven-common";

import { CancelOrProceedActions } from "../CancelOrProceedActions/CancelOrProceedActions.js";

const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "long" });

/**
 * Whether a deletion landed. One `failed` for every way it did not: nothing
 * the person in front of the dialog could do differently tells a refusal from
 * an unreachable server.
 */
export type DeletionOutcome = "deleted" | "failed";

/** How far the assessment has got, or that it could not be told. */
export type ProgressReading =
  ({ kind: "read" } & FormProgress) | { kind: "unavailable" };

/**
 * The message key a failed parse is refused with, or `undefined` where it
 * succeeded.
 */
const reasonError = (
  parsed: z.ZodSafeParseResult<unknown>,
): string | undefined =>
  parsed.success ? undefined : parsed.error.issues[0]?.message;

/**
 * How far `assessment` has got, read each time the dialog opens on it. Not
 * known while it is being read, and no longer wanted once the dialog has
 * moved on to another assessment or closed.
 */
const useProgressOf = (
  assessment: Assessment | null,
  readProgress: (assessmentId: AssessmentId) => Promisable<ProgressReading>,
): ProgressReading | undefined => {
  const [reading, setReading] = useState<
    { assessmentId: AssessmentId; progress: ProgressReading } | undefined
  >(undefined);
  const assessmentId = assessment?.id;

  // Closing forgets what was read, so opening again on the same assessment
  // waits for a fresh reading rather than showing the last one.
  if (assessmentId === undefined && reading !== undefined) {
    setReading(undefined);
  }

  // Read once per assessment the dialog opens on, not again for each new
  // handler a parent's render hands over.
  const read = useEffectEvent(readProgress);

  useEffect(() => {
    if (assessmentId === undefined) {
      return undefined;
    }

    let wanted = true;
    void (async () => {
      let progress: ProgressReading;

      try {
        progress = await read(assessmentId);
      } catch {
        progress = { kind: "unavailable" };
      }

      if (wanted) {
        setReading({ assessmentId, progress });
      }
    })();

    return () => {
      wanted = false;
    };
  }, [assessmentId]);

  return reading?.assessmentId === assessmentId ? reading?.progress : undefined;
};

/**
 * Says what deleting an assessment would take away, asks why it is being
 * deleted, and deletes it.
 *
 * How much work is in it is read when the dialog opens, so the cost of
 * deleting is in front of whoever confirms it; where that cannot be read, the
 * dialog says the rest rather than holding the deletion back.
 */
export const DeleteAssessmentDialog = ({
  assessment,
  assessorName,
  personName,
  readProgress,
  onClose,
  onDelete,
}: {
  /** The assessment to delete, or `null` while the dialog is closed. */
  assessment: Assessment | null;
  /** Who started it; absent where that cannot be told. */
  assessorName: string | undefined;
  /** Whose record it is on; absent, or blank, where that cannot be told. */
  personName: string | undefined;
  /** Called once each time the dialog opens on an assessment; a throw reads as unavailable. */
  readProgress: (assessmentId: AssessmentId) => Promisable<ProgressReading>;
  onClose: () => Promisable<void>;
  onDelete: (deletion: {
    assessmentId: AssessmentId;
    request: DeleteAssessmentRequest;
  }) => Promisable<DeletionOutcome>;
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [reasonTouched, setReasonTouched] = useState(false);
  const [status, setStatus] = useState<"submitting" | "failed" | undefined>(
    undefined,
  );
  const progress = useProgressOf(assessment, readProgress);

  // The server's own schema, so the dialog cannot send a reason it refuses.
  const parsed = DeleteAssessmentRequestSchema.safeParse({ reason });
  const error = reasonError(parsed);
  const given = reason.trim() !== "";
  const submitting = status === "submitting";

  const close = () => {
    setReason("");
    setReasonTouched(false);
    setStatus(undefined);
    void onClose();
  };

  const submit = async () => {
    setReasonTouched(true);

    if (assessment === null || !parsed.success) {
      return;
    }

    setStatus("submitting");
    let outcome: DeletionOutcome;

    try {
      outcome = await onDelete({
        assessmentId: assessment.id,
        request: parsed.data,
      });
    } catch {
      outcome = "failed";
    }

    if (outcome === "deleted") {
      close();
      return;
    }

    setStatus("failed");
  };

  // A record can hold no name, or one with nothing in it, so a blank name is
  // as untold as an absent one.
  const whose =
    personName === undefined || personName.trim() === ""
      ? undefined
      : personName;

  const howFar =
    progress === undefined
      ? undefined
      : match(progress)
          .with({ kind: "read" }, ({ completed, included }) =>
            t("personPage.assessments.deleteDialog.progress", {
              completed,
              included,
            }),
          )
          .with({ kind: "unavailable" }, () => undefined)
          .exhaustive();

  return (
    <Dialog
      open={assessment !== null}
      // Nothing closes the dialog while a deletion is in flight: it cannot
      // be called back once sent, and a dialog closed under it could be
      // reopened for another assessment that its answer would then close.
      onClose={submitting ? undefined : close}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {t("personPage.assessments.deleteDialog.title")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {assessment === null ? null : (
            <DialogContentText>
              {[
                t("personPage.assessments.deleteDialog.started", {
                  assessor:
                    assessorName ?? t("personPage.assessments.unknownAssessor"),
                  date: CALENDAR_DATE.format(assessment.startedAt),
                }),
                howFar,
              ]
                .filter((sentence) => sentence !== undefined)
                .join(" ")}
            </DialogContentText>
          )}
          <Alert severity="warning">
            <Stack spacing={1}>
              <span>
                {whose === undefined
                  ? t("personPage.assessments.deleteDialog.removedFromUnnamed")
                  : t("personPage.assessments.deleteDialog.removedFrom", {
                      personName: whose,
                    })}
              </span>
              <span>
                {t("personPage.assessments.deleteDialog.cannotBeUndone")}
              </span>
            </Stack>
          </Alert>
          <TextField
            id="delete-assessment-reason"
            label={t("personPage.assessments.deleteDialog.reason")}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            onBlur={() => {
              setReasonTouched(true);
            }}
            error={reasonTouched && given && error !== undefined}
            helperText={
              reasonTouched && given && error !== undefined
                ? t(error)
                : undefined
            }
            multiline
            minRows={2}
            fullWidth
            required
          />
          {status === "failed" ? (
            <Alert severity="error">
              {t("personPage.assessments.deleteDialog.failed")}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          onClick: close,
          disabled: submitting,
        }}
        proceed={{
          label: t("personPage.assessments.deleteDialog.confirm"),
          onClick: submit,
          disabled: !given || submitting,
          kind: "destructive",
        }}
      />
    </Dialog>
  );
};
